import { UIMessage } from 'ai';

/**
 * AI SDK v5 UI messages store displayable content in `message.parts`.
 * This extracts all text parts into a single string (works for streaming too).
 */
export function getMessageText(message: UIMessage): string {
  const parts: any[] = Array.isArray((message as any)?.parts)
    ? ((message as any).parts as any[])
    : [];

  return parts
    .map(part => {
      // Text part shape in v5 is `part.type === 'text'` + `part.text` (string)
      if (part?.type === 'text' && typeof part.text === 'string')
        return part.text;

      // Defensive: some app-level wrappers might store `text.value`
      if (part?.type === 'text' && typeof part.text?.value === 'string')
        return part.text.value;

      return '';
    })
    .join('');
}

/** Strips a trailing partial <edit> or <text-edit> tag still arriving mid-stream. */
export function trimPartialTag(text: string): string {
  return text.replace(/<(?:edit|text-?)$/, '');
}

export type MessageChunk = {
  type: 'text' | 'edit' | 'text-edit';
  content: string;
  complete: boolean;
  key: string;
  index: number;
};

/**
 * Streaming-aware parser: operates on the combined text extracted from parts.
 * Parses message content for:
 *   - <edit>...</edit>      — full screen JSON replacement blocks
 *   - <text-edit>...</text-edit>  — atomic text replacement blocks { screenId, oldText, newText }
 */
export function parseMessageContent(content: string): MessageChunk[] {
  const chunks: MessageChunk[] = [];

  let cursor = 0;
  let seq = 0;

  while (cursor < content.length) {
    const editIdx = content.indexOf('<edit>', cursor);
    const textEditIdx = content.indexOf('<text-edit>', cursor);

    // Find whichever open tag comes first
    let openIdx: number;
    let openTag: string;
    let closeTag: string;
    let chunkType: 'edit' | 'text-edit';

    if (editIdx === -1 && textEditIdx === -1) {
      // No more edit blocks — emit remaining text
      const text = content.slice(cursor);
      if (text) {
        chunks.push({
          type: 'text',
          content: text,
          complete: true,
          key: `text-${seq}`,
          index: seq
        });
        seq++;
      }
      break;
    } else if (
      editIdx !== -1 &&
      (textEditIdx === -1 || editIdx < textEditIdx)
    ) {
      openIdx = editIdx;
      openTag = '<edit>';
      closeTag = '</edit>';
      chunkType = 'edit';
    } else {
      openIdx = textEditIdx;
      openTag = '<text-edit>';
      closeTag = '</text-edit>';
      chunkType = 'text-edit';
    }

    // Emit any text before the open tag
    if (openIdx > cursor) {
      const text = content.slice(cursor, openIdx);
      chunks.push({
        type: 'text',
        content: text,
        complete: true,
        key: `text-${seq}`,
        index: seq
      });
      seq++;
    }

    const startContentIdx = openIdx + openTag.length;
    const closeIdx = content.indexOf(closeTag, startContentIdx);

    if (closeIdx === -1) {
      // Block is still streaming — emit as incomplete
      const partial = content.slice(startContentIdx);
      chunks.push({
        type: chunkType,
        content: partial,
        complete: false,
        key: `${chunkType}-${seq}`,
        index: seq
      });
      break;
    } else {
      const inner = content.slice(startContentIdx, closeIdx);
      chunks.push({
        type: chunkType,
        content: inner,
        complete: true,
        key: `${chunkType}-${seq}`,
        index: seq
      });
      cursor = closeIdx + closeTag.length;
      seq++;
      continue;
    }
  }

  return chunks;
}
